import React, { useState, useEffect } from 'react';
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import API_CONFIG from '../config/api';
import { UsersData, ChartDataItem, UserProfileDetails } from '../types/api';
import { requestManager } from '../utils/requestManager';
import { cacheEventManager } from '../utils/cacheEventManager';

const PROFILE_COMPANY_KEYS = ['company_name', 'companyName', 'company'];
const PROFILE_PHONE_KEYS = ['phone_number', 'phoneNumber', 'phone', 'mobile', 'mobile_number'];

const pickProfileValue = (profile: any, keys: string[]): string => {
    for (const key of keys) {
        const value = profile?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== '') {
            return String(value);
        }
    }
    return 'N/A';
};

interface UserProfileDetailsViewProps {
    userId: string;
}

const UserProfileDetailsView: React.FC<UserProfileDetailsViewProps> = ({ userId }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [details, setDetails] = useState<UserProfileDetails | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        const fetchProfileDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.USER.PROFILE_DETAILS}`;
                const response = await requestManager.makeRequest(url, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ userId })
                });
                if (!isMounted) return;
                setDetails(response?.data ?? response);
            } catch (err) {
                console.error('Error fetching user profile details:', err);
                if (isMounted) setError('Unable to load company & phone details');
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchProfileDetails();

        return () => {
            isMounted = false;
        };
    }, [userId]);

    if (loading) {
        return (
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500"></div>
                <span>Loading details...</span>
            </div>
        );
    }

    if (error) {
        return <div className="text-sm text-red-500 dark:text-red-400">{error}</div>;
    }

    const profile = details?.profile ?? details?.profileDetails ?? details ?? {};
    const companyName = pickProfileValue(profile, PROFILE_COMPANY_KEYS);
    const phoneNumber = pickProfileValue(profile, PROFILE_PHONE_KEYS);

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Company Name</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1 break-words">{companyName}</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Phone Number</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1 break-words">{phoneNumber}</p>
            </div>
        </div>
    );
};

interface UsersProps {
    timeRange: string;
}

const Users: React.FC<UsersProps> = ({ timeRange }) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [usersData, setUsersData] = useState<UsersData | null>(null);

    // State for collapsible sections
    const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({
        donutCharts: false,
        barChartAndSummary: false,
        paidUsersList: false,
        newUsersList: false,
        activeUsersList: false
    });

    // Toggle function for collapsible sections
    const toggleSection = (sectionKey: string) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionKey]: !prev[sectionKey]
        }));
    };

    // Track which user's profile details are expanded
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);

    const toggleUserDetails = (userId: string) => {
        setExpandedUserId(prev => (prev === userId ? null : userId));
    };

    // API Helper Function with request deduplication
    const apiCall = async (endpoint: string) => {
        try {
            const url = `${API_CONFIG.BASE_URL}${endpoint}?period=${timeRange}`;
            const data = await requestManager.makeRequest(url);

            if (!data.success) throw new Error(data.message || 'API call failed');
            return data.data;
        } catch (error) {
            console.error(`API Error for ${endpoint}:`, error);
            throw error;
        }
    };

    const fetchUsersData = async () => {
        try {
            setLoading(true);
            const data = await apiCall(API_CONFIG.ENDPOINTS.ANALYTICS.USERS);
            setUsersData(data);
        } catch (error) {
            console.error('Error fetching users data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsersData();
    }, [timeRange]);

    // Listen for cache clear events to auto-refresh data
    useEffect(() => {
        const unsubscribe = cacheEventManager.subscribe(() => {
            fetchUsersData();
        });

        return unsubscribe;
    }, [timeRange]);

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-orange-500"></div>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Loading users data...</p>
                </div>
            </div>
        );
    }

    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4'];

    // Prepare data for charts
    const userTypeData = usersData ? [
        { name: 'Paid Users', value: usersData.total_paid_users || 0, color: COLORS[0] },
        { name: 'Free Users', value: usersData.total_free_users || 0, color: COLORS[1] }
    ] : [];


    const paidUsersBreakdown = usersData ? [
        { name: 'Indian Paid', value: usersData.total_indian_paid_users || 0, color: COLORS[0] },
        { name: 'Foreigner Paid', value: usersData.total_foreigner_paid_users || 0, color: COLORS[2] }
    ] : [];

    const barData = usersData ? [
        { category: 'Indian Paid', count: usersData.total_indian_paid_users || 0, type: 'Paid' },
        { category: 'Indian Free', count: usersData.total_indian_free_users || 0, type: 'Free' },
        { category: 'Foreigner Paid', count: usersData.total_foreigner_paid_users || 0, type: 'Paid' },
        { category: 'Foreigner Free', count: usersData.total_foreigner_free_users || 0, type: 'Free' }
    ] : [];

    // Collapsible Header Component
    const CollapsibleHeader: React.FC<{
        title: string;
        count?: number;
        color: string;
        isCollapsed: boolean;
        onToggle: () => void;
    }> = ({ title, count, color, isCollapsed, onToggle }) => (
        <div
            className="flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2 -m-2 transition-colors"
            onClick={onToggle}
        >
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center">
                <div className={`w-4 h-4 ${color} rounded-full mr-2`}></div>
                {title} {count !== undefined && `(${count})`}
            </h3>
            <div className="flex items-center">
                <svg
                    className={`w-5 h-5 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''
                        }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>
        </div>
    );

    return (
        <div className="flex-1 flex flex-col min-h-0">
            {/* Content */}
            <main className="flex-1 overflow-y-auto p-6 bg-gray-50 dark:bg-gray-900">
                {/* Summary Cards - Single Row Layout */}
                <div className="grid grid-cols-5 gap-4 mb-8">
                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Total Users</p>
                        <p className="text-2xl font-bold text-gray-800 dark:text-white mt-1">
                            {usersData?.total_users?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Paid Users</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">
                            {usersData?.total_paid_users?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Free Users</p>
                        <p className="text-2xl font-bold text-cyan-600 mt-1">
                            {usersData?.total_free_users?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">New Users</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">
                            {usersData?.new_users?.toLocaleString() || 0}
                        </p>
                    </div>

                    <div className="glass-card p-4">
                        <p className="text-gray-500 dark:text-gray-400 text-xs font-medium uppercase tracking-wide">Active Users</p>
                        <p className="text-2xl font-bold text-emerald-600 mt-1">
                            {usersData?.active_users?.toLocaleString() || 0}
                        </p>
                    </div>
                </div>

                {/* All Donut Charts - Single Row Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="User Distribution Charts"
                        isCollapsed={collapsedSections.donutCharts}
                        onToggle={() => toggleSection('donutCharts')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.donutCharts && (
                        <div className="flex gap-6 mt-6">
                            {/* User Type Distribution - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">User Type Distribution</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={userTypeData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {userTypeData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* Paid Users Breakdown - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">Paid Users by Region</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={paidUsersBreakdown}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={120}
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(1)}%)`}
                                            >
                                                {paidUsersBreakdown.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), name]}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Bar Chart and Summary - Side by Side Layout */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="User Analytics & Demographics"
                        isCollapsed={collapsedSections.barChartAndSummary}
                        onToggle={() => toggleSection('barChartAndSummary')}
                        color="bg-rose-500"
                    />

                    {!collapsedSections.barChartAndSummary && (
                        <div className="flex gap-6 mt-6">
                            {/* User Categories Breakdown - Left Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">User Categories Breakdown</h4>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={barData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                                dataKey="category"
                                                angle={-45}
                                                textAnchor="end"
                                                height={100}
                                                tick={{ fontSize: 12 }}
                                            />
                                            <YAxis tick={{ fontSize: 12 }} />
                                            <Tooltip
                                                formatter={(value, name) => [value.toLocaleString(), 'Users']}
                                                contentStyle={{
                                                    backgroundColor: 'white',
                                                    border: '1px solid #e5e7eb',
                                                    borderRadius: '8px'
                                                }}
                                            />
                                            <Bar dataKey="count">
                                                {barData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>

                            {/* User Demographics Summary - Right Half */}
                            <div className="w-1/2">
                                <h4 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-4">User Demographics Summary</h4>
                                <div className="grid grid-cols-2 gap-8 h-80">
                                    <div className="text-center flex flex-col justify-center">
                                        <div className="text-4xl font-bold text-orange-600 mb-2">{usersData?.total_indian_paid_users || 0}</div>
                                        <div className="text-base font-medium text-gray-600 dark:text-gray-400">Indian Paid</div>
                                    </div>
                                    <div className="text-center flex flex-col justify-center">
                                        <div className="text-4xl font-bold text-orange-600 mb-2">{usersData?.total_indian_free_users || 0}</div>
                                        <div className="text-base font-medium text-gray-600 dark:text-gray-400">Indian Free</div>
                                    </div>
                                    <div className="text-center flex flex-col justify-center">
                                        <div className="text-4xl font-bold text-orange-600 mb-2">{usersData?.total_foreigner_paid_users || 0}</div>
                                        <div className="text-base font-medium text-gray-600 dark:text-gray-400">Foreigner Paid</div>
                                    </div>
                                    <div className="text-center flex flex-col justify-center">
                                        <div className="text-4xl font-bold text-orange-600 mb-2">{usersData?.total_foreigner_free_users || 0}</div>
                                        <div className="text-base font-medium text-gray-600 dark:text-gray-400">Foreigner Free</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Paid Users List */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Paid Users Details"
                        count={usersData?.paid_users?.length || 0}
                        isCollapsed={collapsedSections.paidUsersList}
                        onToggle={() => toggleSection('paidUsersList')}
                        color="bg-green-500"
                    />

                    {!collapsedSections.paidUsersList && (
                        <div className="mt-6">
                            {usersData?.paid_users && usersData.paid_users.length > 0 ? (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Details
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {usersData.paid_users.map((user, index) => (
                                                <React.Fragment key={user.user_id}>
                                                    <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <div className="flex items-center">
                                                                <div className="flex-shrink-0 h-10 w-10">
                                                                    <div className="h-10 w-10 rounded-full bg-green-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                        {(user.name || 'U').charAt(0).toUpperCase()}
                                                                    </div>
                                                                </div>
                                                                <div className="ml-4">
                                                                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                        {user.name || 'N/A'}
                                                                    </div>
                                                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                        {user.user_id || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            {user.email}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                                Paid User
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                            <button
                                                                type="button"
                                                                onClick={() => toggleUserDetails(user.user_id)}
                                                                className="font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                                                            >
                                                                {expandedUserId === user.user_id ? 'Hide Details' : 'View Details'}
                                                            </button>
                                                        </td>
                                                    </tr>
                                                    {expandedUserId === user.user_id && (
                                                        <tr className="bg-gray-50/60 dark:bg-gray-800/40">
                                                            <td colSpan={4} className="px-6 py-4">
                                                                <UserProfileDetailsView userId={user.user_id} />
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <div className="text-gray-500 dark:text-gray-400">
                                        No paid users found for the selected period.
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* New Users List */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="New Users Details"
                        count={usersData?.new_users_data?.length || 0}
                        isCollapsed={collapsedSections.newUsersList}
                        onToggle={() => toggleSection('newUsersList')}
                        color="bg-orange-500"
                    />

                    {!collapsedSections.newUsersList && (
                        <div className="mt-6">
                            {usersData?.new_users_data && usersData.new_users_data.length > 0 ? (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Registration Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Details
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {usersData.new_users_data
                                                .slice()
                                                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                                .map((user, index) => (
                                                    <React.Fragment key={user.user_id}>
                                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0 h-10 w-10">
                                                                        <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-semibold text-sm">
                                                                            {(user.name || 'U').charAt(0).toUpperCase()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                            {user.name || 'N/A'}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {user.user_id || 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {user.email}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                                                    New User
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {user.timestamp ? new Date(user.timestamp).toLocaleString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                }) : 'N/A'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleUserDetails(user.user_id)}
                                                                    className="font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                                                                >
                                                                    {expandedUserId === user.user_id ? 'Hide Details' : 'View Details'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {expandedUserId === user.user_id && (
                                                            <tr className="bg-gray-50/60 dark:bg-gray-800/40">
                                                                <td colSpan={5} className="px-6 py-4">
                                                                    <UserProfileDetailsView userId={user.user_id} />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">No new users found.</div>
                            )}
                        </div>
                    )}
                </div>

                {/* Active Users List */}
                <div className="glass-card p-6 mb-8">
                    <CollapsibleHeader
                        title="Active Users Details"
                        count={usersData?.active_users_data?.length || 0}
                        isCollapsed={collapsedSections.activeUsersList}
                        onToggle={() => toggleSection('activeUsersList')}
                        color="bg-emerald-500"
                    />

                    {!collapsedSections.activeUsersList && (
                        <div className="mt-6">
                            {usersData?.active_users_data && usersData.active_users_data.length > 0 ? (
                                <div className="overflow-x-auto max-h-96 overflow-y-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                        <thead>
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Email
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    User Type
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Last Activity
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                                    Details
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-200/60 dark:divide-white/10">
                                            {usersData.active_users_data
                                                .slice()
                                                .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                                .map((user, index) => (
                                                    <React.Fragment key={user.user_id}>
                                                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="flex items-center">
                                                                    <div className="flex-shrink-0 h-10 w-10">
                                                                        <div className="h-10 w-10 rounded-full bg-emerald-400 flex items-center justify-center text-white font-semibold text-sm">
                                                                            {(user.name || 'U').charAt(0).toUpperCase()}
                                                                        </div>
                                                                    </div>
                                                                    <div className="ml-4">
                                                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                                                            {user.name || 'N/A'}
                                                                        </div>
                                                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                                                            {user.user_id || 'N/A'}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {user.email}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
                                                                    Active User
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                                {user.timestamp ? new Date(user.timestamp).toLocaleString('en-US', {
                                                                    year: 'numeric',
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                }) : 'N/A'}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleUserDetails(user.user_id)}
                                                                    className="font-medium text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300"
                                                                >
                                                                    {expandedUserId === user.user_id ? 'Hide Details' : 'View Details'}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                        {expandedUserId === user.user_id && (
                                                            <tr className="bg-gray-50/60 dark:bg-gray-800/40">
                                                                <td colSpan={5} className="px-6 py-4">
                                                                    <UserProfileDetailsView userId={user.user_id} />
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">No active users found.</div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Users;
